import {type HgHeroCTAProps} from '~/design-system/hg/modules'
import {getEmailCaptureDestinationProps} from '~/cms/helpers/getEmailCaptureProps/getEmailCaptureDestinationProps'
import {getLinkProps} from '~/cms/components/LinkComponent'
import {
  type Node,
  type Root,
  type StructuredText,
  isDocument,
} from 'datocms-structured-text-utils'
import {
  type PartnerPageLogo,
  type HeroSectionProps,
  type LogoAssetLogo,
} from '../_types'
import {type CSSProperties} from 'react'
import LogoSVG from '~/cms/modules/_FEM/LogoMash/LogoSVG'
import {Image as ReactDatoImage} from 'react-datocms'
import {
  type DefaultCinematicOverlayImageFragment,
  type DefaultCinematicOverlayVideoFragment,
  type HeroFragment,
  type DefaultCinematicStackedImageFragment,
  type DefaultCinematicStackedVideoFragment,
  type HeroBulletsFragment,
} from '~/cms/pages/FEMPage/_components/HeroSection/_generated/fragments.graphql'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import {screens} from '~/design-system/hg/tokens'
import CinematicResponsiveImage, {
  CinematicResponsiveMaskImage,
} from '../_components/CinematicResponsiveImage'
import CinematicResponsiveVideo from '../_components/CinematicResponsiveVideo'
import {type HgCinematicHeroProps} from '~/design-system/hg/modules/HgHero/_components/CinematicHero'
import {
  type CinematicStackedMobileAssetSize,
  type CinematicStackedDesktopAssetSize,
} from '~/design-system/hg/modules/HgHero/_components/CinematicAssetVariant/types'
import {
  type HgHeroCinematicProps,
  type OverlayVariant,
  type StackedVariant,
} from '~/design-system/hg/modules/HgHero/_components/CinematicHero/types'
import {
  type AuraColorToken,
  getHexValueFromToken,
} from '~/design-system/hg/tokens/colors'
import {SingleLineStructuredText} from '~/cms/components/SingleLineStructuredText'
import {type HgIconType} from '~/design-system/hg/components'

export function getCTATypeProps(data: HeroSectionProps['data']): HgHeroCTAProps {
  if (data.ctaType === 'email_capture' && data.emailCapture) {
    return {
      contentType: 'emailCapture',
      emailCaptureProps: getEmailCaptureDestinationProps(data.emailCapture),
    }
  }

  return {
    contentType: 'buttonGroup',
    primaryButtonProps: getLinkProps(data.primaryButton),
    secondaryButtonProps: getLinkProps(data.secondaryButton),
  }
}

export function structuredTextCharacterCount(structuredText: StructuredText) {
  let totalCharacters = 0

  function countCharacters(node: Node | Root) {
    if ('value' in node && node.value) {
      totalCharacters += node.value.length
    }

    if ('children' in node && node.children) {
      node.children.forEach(child => {
        countCharacters(child)
      })
    }
  }

  if (isDocument(structuredText.value)) {
    countCharacters(structuredText.value.document)
  }

  return totalCharacters
}

export function getLogo(logoData?: PartnerPageLogo) {
  if (!logoData) {
    return
  }

  if (typeof logoData === 'string') {
    return logoData
  }

  if (logoData.logo.format === 'svg') {
    return (
      <div
        style={
          {
            '--height': `${typeof logoData.height === 'number' ? logoData.height : 24}px`,
          } as CSSProperties
        }
        className="[&_svg]:h-[var(--height)] [&_svg]:w-full"
      >
        <LogoSVG logo={logoData} />
      </div>
    )
  }

  if (!logoData.logo.logoResponsiveImage) {
    return
  }

  return (
    <ReactDatoImage
      priority
      data={{...logoData.logo.logoResponsiveImage, alt: logoData.companyName}}
      imgStyle={{height: logoData.height ?? '24px', width: 'auto'}}
    />
  )
}

export function getLogoFromLogoAsset(logoData?: LogoAssetLogo) {
  if (!logoData) {
    return
  }

  if (typeof logoData === 'string') {
    return logoData
  }

  if (logoData.logo.logo.format === 'svg') {
    const height: number = logoData.logo.logo.height ?? 24
    return (
      <div
        style={
          {
            '--height': `${height}px`,
          } as CSSProperties
        }
        className="[&_svg]:h-[var(--height)] [&_svg]:w-full"
      >
        <LogoSVG
          logo={{
            id: logoData.id,
            companyName: logoData.logo.companyName,
            logo: {
              id: logoData.logo.logo.id,
              url: logoData.logo.logo.url,
              width: logoData.logo.logo.width,
              height: logoData.logo.logo.height,
            },
          }}
        />
      </div>
    )
  }

  if (!logoData.logo.logo.logoResponsiveImage) {
    return
  }

  return (
    <ReactDatoImage
      priority
      data={{
        ...logoData.logo.logo.logoResponsiveImage,
        alt: logoData.logo.companyName,
      }}
    />
  )
}

// Helper function to figure out if the default variant is using an image or video asset
export function renderDefaultAsset(
  asset:
    | DefaultCinematicOverlayImageFragment
    | DefaultCinematicOverlayVideoFragment
    | (DefaultCinematicStackedImageFragment & {
        desktopAssetSize: CinematicStackedDesktopAssetSize
        mobileAssetSize: CinematicStackedMobileAssetSize
      })
    | (DefaultCinematicStackedVideoFragment & {
        desktopAssetSize: CinematicStackedDesktopAssetSize
        mobileAssetSize: CinematicStackedMobileAssetSize
      })
) {
  switch (asset.__typename) {
    case 'DefaultCinematicOverlayImageComponentRecord':
      return (
        <CinematicResponsiveImage
          mobileImage={asset.mobileImage}
          tabletImage={asset.tabletImage}
          desktopImage={asset.desktopImage}
        />
      )
    case 'DefaultCinematicOverlayVideoComponentRecord':
      return (
        <CinematicResponsiveVideo
          variant="overlay"
          loop={!!asset.loopVideos}
          mobileVideo={
            asset.mobileVideo
              ? {...asset.mobileVideo, aspectRatio: '9:16'}
              : undefined
          }
          tabletVideo={
            asset.tabletVideo
              ? {...asset.tabletVideo, aspectRatio: '4:5'}
              : undefined
          }
          desktopVideo={
            asset.desktopVideo
              ? {...asset.desktopVideo, aspectRatio: '16:9'}
              : undefined
          }
        />
      )
    case 'DefaultCinematicStackedImageComponentRecord': {
      return (
        <CinematicResponsiveImage
          mobileImage={
            asset.mobileAssetSize === 'long'
              ? asset.mobileImage
              : asset.mobileImageShort
          }
          tabletImage={asset.tabletImage}
          desktopImage={
            asset.desktopAssetSize === 'long'
              ? asset.desktopLongImage || undefined
              : asset.desktopShortImage || undefined
          }
        />
      )
    }
    case 'DefaultCinematicStackedVideoComponentRecord': {
      return (
        <CinematicResponsiveVideo
          variant="stacked"
          loop={!!asset.loopVideos}
          mobileVideo={
            asset.mobileVideo
              ? {
                  ...asset.mobileVideo,
                  aspectRatio: asset.mobileAssetSize === 'long' ? '4:5' : '16:9',
                }
              : undefined
          }
          tabletVideo={
            asset.tabletVideo
              ? {...asset.tabletVideo, aspectRatio: '16:5'}
              : undefined
          }
          desktopVideo={
            asset.desktopAssetSize === 'long' && asset.desktopLongVideo
              ? {...asset.desktopLongVideo, aspectRatio: '21:9'}
              : asset.desktopAssetSize === 'short' && asset.desktopShortVideo
                ? {...asset.desktopShortVideo, aspectRatio: '16:5'}
                : undefined
          }
        />
      )
    }
    default:
      return null
  }
}

export function getCinematicHeroProps(
  variant: Extract<
    HeroFragment['variant'],
    | {__typename: 'CinematicHeroComponentRecord'}
    | {__typename: 'CinematicOverlayComponentRecord'}
    | {__typename: 'CinematicStackedComponentRecord'}
  >
) {
  switch (variant.__typename) {
    // TODO: Remove this case when the old variant is removed
    case 'CinematicHeroComponentRecord':
      return {
        variant: 'old' as const,
        mobileAssetSize: 'old' as const,
        backgroundAsset: (
          <picture>
            <source
              media={`(min-width: ${screens['3xl']})`}
              srcSet={
                variant.heroSixteenNineBackgroundImage?.image?.responsiveImage
                  ?.srcSet || ''
              }
              sizes={
                variant.heroSixteenNineBackgroundImage?.image?.responsiveImage
                  ?.sizes || ''
              }
            />
            {/* lg breakpoint */}
            <source
              media={`(min-width: ${screens.lg})`}
              srcSet={
                variant.heroThreeTwoBackgroundImage?.image?.responsiveImage
                  ?.srcSet || ''
              }
              sizes={
                variant.heroThreeTwoBackgroundImage?.image?.responsiveImage?.sizes ||
                ''
              }
            />
            {/* sm breakpoint */}
            <source
              media={`(min-width: ${screens.sm})`}
              srcSet={
                variant.heroFourFiveBackgroundImage?.image?.responsiveImage
                  ?.srcSet || ''
              }
              sizes={
                variant.heroFourFiveBackgroundImage?.image?.responsiveImage?.sizes ||
                ''
              }
            />
            {/* Fallback image */}
            <img
              src={variant.mobileMaskImage?.image?.responsiveImage?.src || ''}
              alt={variant.mobileMaskImage?.image?.responsiveImage?.alt || ''}
              className="h-full object-cover"
              fetchPriority="high"
              loading="eager"
            />
          </picture>
        ),
        maskImage: (
          <picture>
            <source
              media={`(min-width: ${screens['3xl']})`}
              srcSet={
                variant.heroSixteenNineMaskImage?.image?.responsiveImage?.srcSet ||
                ''
              }
              sizes={
                variant.heroSixteenNineMaskImage?.image?.responsiveImage?.sizes || ''
              }
            />
            {/* lg breakpoint */}
            <source
              media={`(min-width: ${screens.lg})`}
              srcSet={
                variant.heroThreeTwoMaskImage?.image?.responsiveImage?.srcSet || ''
              }
              sizes={
                variant.heroThreeTwoMaskImage?.image?.responsiveImage?.sizes || ''
              }
            />
            {/* The mask effect is not used below desktop screens, so no we just use a transparent placeholder. This ensures that if we're not using the mask image, it won't be loaded and won't send additional network requests. */}
            <img
              src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              alt={variant.heroFourFiveMaskImage?.image?.responsiveImage?.alt || ''}
              className="h-full object-cover"
              fetchPriority="high"
              loading="eager"
            />
          </picture>
        ),
      }
    case 'CinematicOverlayComponentRecord': {
      const overlay = variant.overlayVariant
      const cinematicVariant: HgCinematicHeroProps['variant'] = 'overlay'
      if (!overlay) return null

      if (overlay.__typename === 'MaskCinematicOverlayComponentRecord') {
        return {
          variant: cinematicVariant,
          backgroundAsset: (
            <CinematicResponsiveImage
              mobileImage={overlay.mobileImage}
              tabletImage={overlay.tabletImage}
              desktopImage={overlay.desktopImage}
            />
          ),
          maskImage: overlay.desktopMaskImage?.image?.responsiveImage ? (
            <CinematicResponsiveMaskImage desktopImage={overlay.desktopMaskImage} />
          ) : null,
        } satisfies Omit<Extract<HgHeroCinematicProps, OverlayVariant>, 'type'>
      } else if (overlay.__typename === 'DefaultCinematicOverlayComponentRecord') {
        const assetType: HgCinematicHeroProps['assetType'] =
          overlay.asset?.__typename === 'DefaultCinematicOverlayImageComponentRecord'
            ? 'image'
            : 'video'
        return {
          variant: cinematicVariant,
          assetType,
          backgroundAsset: overlay.asset ? renderDefaultAsset(overlay.asset) : null,
          maskImage: null,
        } satisfies Omit<Extract<HgHeroCinematicProps, OverlayVariant>, 'type'>
      }
      return null
    }
    case 'CinematicStackedComponentRecord': {
      const stacked = variant.stackedVariant
      const cinematicVariant: HgCinematicHeroProps['variant'] = 'stacked'
      const desktopAssetSize =
        variant.desktopAssetSize as CinematicStackedDesktopAssetSize
      if (!stacked) return null

      if (stacked.__typename === 'MaskCinematicStackedComponentRecord') {
        return {
          variant: cinematicVariant,
          desktopAssetSize,
          applyGrain: !!variant.applyGrain,
          backgroundColor: getHexValueFromToken(
            variant.backgroundColor as AuraColorToken
          ),
          backgroundAsset: (
            <CinematicResponsiveImage
              mobileImage={
                variant.mobileAssetSize === 'long'
                  ? stacked.mobileImage
                  : stacked.mobileImageShort
              }
              tabletImage={stacked.tabletImage}
              desktopImage={
                variant.desktopAssetSize === 'long'
                  ? stacked.desktopLongImage || undefined
                  : stacked.desktopShortImage || undefined
              }
            />
          ),
          maskImage: (
            <CinematicResponsiveMaskImage
              desktopImage={
                variant.desktopAssetSize === 'long'
                  ? stacked.desktopLongMaskImage || undefined
                  : stacked.desktopShortMaskImage || undefined
              }
            />
          ),
          mobileAssetSize: (variant.mobileAssetSize ??
            'long') as CinematicStackedMobileAssetSize,
        } satisfies Omit<Extract<HgHeroCinematicProps, StackedVariant>, 'type'>
      } else if (stacked.__typename === 'DefaultCinematicStackedComponentRecord') {
        const assetType: HgCinematicHeroProps['assetType'] =
          stacked.asset?.__typename === 'DefaultCinematicStackedImageComponentRecord'
            ? 'image'
            : 'video'
        return {
          variant: cinematicVariant,
          desktopAssetSize,
          applyGrain: !!variant.applyGrain,
          assetType,
          backgroundColor: getHexValueFromToken(
            variant.backgroundColor as AuraColorToken
          ),
          backgroundAsset: stacked.asset
            ? renderDefaultAsset({
                ...stacked.asset,
                desktopAssetSize:
                  variant.desktopAssetSize as CinematicStackedDesktopAssetSize,
                mobileAssetSize: (variant.mobileAssetSize ??
                  'long') as CinematicStackedMobileAssetSize,
              })
            : null,
          maskImage: null,
          mobileAssetSize: (variant.mobileAssetSize ??
            'long') as CinematicStackedMobileAssetSize,
        } satisfies Omit<Extract<HgHeroCinematicProps, StackedVariant>, 'type'>
      }
      return null
    }
    default:
      return safeUnreachableCase(variant, null)
  }
}

export function generateHeroBullets(heroBullets?: HeroBulletsFragment | null) {
  if (!heroBullets) return undefined
  return heroBullets.heroBulletItems.map(bullet => ({
    id: bullet.id,
    iconType: bullet.icon.iconType as HgIconType,
    text: <SingleLineStructuredText data={bullet.body} />,
  }))
}
